<template>
    <div>
        <form name="editskill">
            <div class="tw-relative">
                <l-input-text
                    ref="input-name"
                    v-model="currentSkill.name"
                    data-test-id="name"
                    type="text"
                    :optional="false"
                    :label="$t('skills.modal.name-label')"
                    :validator="v => v && v.length >= 3"
                    :error-message="$t('skills.modal.name-error')"
                />
                <span
                    v-b-popover.hover.top="$t(`skills.translate.iconPopover`)"
                    class="tw-absolute tw-cursor-pointer tw-text-lg tw-text-gray-400 hover:tw-text-gray-900"
                    style="top: 7px; right: 14px;"
                    tabindex="0"
                    @click="openTranslationModal('name', 'text')"
                    @keyup.enter="openTranslationModal('name', 'text')"
                >
                    <i class="fal fa-language"></i>
                </span>
            </div>

            <l-select
                v-show="['team', 'localTeam'].includes(currentSkill.type)"
                ref="input-category"
                v-model="currentSkill.category"
                :label="$t('skills.modal.category-label')"
                data-test-id="category"
                :options="categories.map(c => ({ value: c._id, text: c.name }))"
                :validator="v => v != null"
                :show-help-block="true"
                :error-message="$t('skills.modal.error-required')"
            />

            <div class="form-group label-floating pb-2">
                <div class="togglebutton">
                    <label data-test-id="isonlyUsedInReviews" for="current-skill-onlyUsedInReviews">
                        <input
                            id="current-skill-onlyUsedInReviews"
                            type="checkbox"
                            :checked="currentSkill.onlyUsedInReviews"
                            @change="toggleOnlyUsedInReviews"
                        />
                        <span class="toggle"></span>
                    </label>
                    <span class="control-label">{{
                        $t("skills.modal.labelOnlyUsedInReviews")
                    }}</span>
                </div>
            </div>

            <div class="form-group label-floating pb-2">
                <div class="togglebutton">
                    <label data-test-id="isTeamSkill" for="current-skill-type">
                        <input
                            id="current-skill-type"
                            type="checkbox"
                            :checked="currentSkill.type === 'localTeam'"
                            @change="changeType"
                        />
                        <span class="toggle"></span>
                    </label>
                    <span class="control-label">{{ $t("skills.modal.team-label-everyone") }}</span>
                </div>
            </div>

            <div v-if="currentSkill.type == 'localTeam'" class="form-group label-floating pb-2">
                <div class="togglebutton">
                    <label
                        data-test-id="isTeamSkillEvaluatedByTeamMembers"
                        for="current-skill-onlyEvaluatedByTeamMembers"
                    >
                        <input
                            id="current-skill-onlyEvaluatedByTeamMembers"
                            type="checkbox"
                            :checked="currentSkill.onlyEvaluatedByTeamMembers"
                            @change="toggleOnlyEvaluatedByTeamMembers"
                        />
                        <span class="toggle"></span>
                    </label>
                    <span class="control-label">{{
                        $t("skills.modal.labelOnlyEvaluatedByTeamMembers")
                    }}</span>
                </div>
            </div>

            <div v-show="currentSkill.type === 'localTeam'" class="form-group label-floating pb-2">
                <l-input-typeahead
                    :label="$t(`skills.modal.teams`)"
                    :placeholder="$t('skills.modal.select-teams')"
                    :data="teams"
                    :on-select="onTeamSelect"
                    :on-input-change="onTeamChange"
                >
                </l-input-typeahead>
                <span
                    v-for="(t, index) in currentSkill.localTeam"
                    v-show="currentSkill.localTeam.length"
                    :key="t._id"
                >
                    <span
                        class="label label-default team"
                        tabindex="0"
                        @click="removeTeam(index)"
                        @keyup.enter="removeTeam(index)"
                    >
                        {{ t.name }} <i class="fa fa-remove"></i>
                    </span>
                </span>
            </div>

            <div class="form-group label-floating tw-relative">
                <l-input-text
                    v-model="currentSkill.description"
                    :rows="3"
                    type="textarea"
                    :optional="true"
                    :label="$t(`skills.modal.default-desc-label`)"
                    :show-text-format-hint="true"
                >
                </l-input-text>
                <span
                    v-b-popover.hover.top="$t(`skills.translate.iconPopover`)"
                    class="tw-absolute tw-cursor-pointer tw-text-lg tw-text-gray-400 hover:tw-text-gray-900"
                    style="bottom:36px; right: 14px;"
                    tabindex="0"
                    @click="openTranslationModal('description', 'textarea')"
                    @keyup.enter="openTranslationModal('description', 'textarea')"
                >
                    <i class="fal fa-language"></i>
                </span>
            </div>
            <div
                v-for="customRole in customRoles"
                :key="customRole._id"
                class="form-group label-floating tw-relative"
            >
                <label class="control-label" :for="`input-skill-description-${customRole._id}`">
                    {{ $t("skills.modal.specific-desc-label") }}
                    <strong>"{{ customRole.name }}"</strong>
                    {{ $t("skills.modal.specific-desc-label-optional") }}
                </label>
                <l-input-text
                    :id="`input-skill-description-${customRole._id}`"
                    v-model="currentSkill.descriptionCustomRoles[customRole._id].main"
                    type="textarea"
                    :optional="true"
                    :label="''"
                    :rows="3"
                    :show-text-format-hint="true"
                >
                </l-input-text>
                <span
                    v-b-popover.hover.top="$t(`skills.translate.iconPopover`)"
                    class="tw-absolute tw-cursor-pointer tw-text-lg tw-text-gray-400 hover:tw-text-gray-900"
                    style="bottom:36px; right: 14px;"
                    tabindex="0"
                    @click="
                        openTranslationModal(
                            `descriptionCustomRoles.${customRole._id}.main`,
                            'textarea',
                        )
                    "
                    @keyup.enter="
                        openTranslationModal(
                            `descriptionCustomRoles.${customRole._id}.main`,
                            'textarea',
                        )
                    "
                >
                    <i class="fal fa-language"></i>
                </span>
            </div>

            <hr style="margin-top: 10px;" />

            <p>
                {{ $t("skills.modal.tip.title") }}
            </p>

            <ul>
                <li v-for="item in $t('skills.modal.tip.items').filter(Boolean)" :key="item">
                    {{ item }}
                </li>
            </ul>
        </form>

        <div class="footer">
            <button class="btn btn-primary" type="button" @click="close">
                {{ $t("skills.modal.btn-cancel") }}
            </button>
            <button
                v-show="!currentSkill._id"
                data-test-id="add"
                class="btn btn-primary btn-raised"
                type="button"
                @click="upsert"
            >
                {{ $t("skills.modal.btn-add-skill") }}
            </button>
            <button
                v-show="currentSkill._id"
                class="btn btn-primary btn-raised"
                type="button"
                data-test-id="update"
                @click="upsert"
            >
                {{ $t("skills.modal.btn-update-skill") }}
            </button>
        </div>

        <l-translation-modal
            v-show="showTranslationModal"
            :existing-translations="translations"
            :input-type="inputType"
            @cancel="showTranslationModal = false"
            @confirm="updateTranslations"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import getVal from "@/utils/get-nested-object-lookup";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSelect from "@/components/LSelect";
import LTranslationModal from "@/components/LTranslationModal";

import icon from "@/img/icons/settings.png";
import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        LInputText,
        LInputTypeahead,
        LSelect,
        LTranslationModal,
    },
    props: {
        close: {
            type: Function,
            required: true, // from router-view
        },
    },
    data() {
        return {
            currentSkill: {},

            showTranslationModal: false,
            translations: {},
            inputType: "",
        };
    },
    computed: {
        ...mapGetters({
            maxOrder: "skills/getMaxOrder",
            getSkillById: "skills/getSkillById",
            categories: "skills/getCategories",
            getCustomRoles: "teams/customrole/list",
            teamsSearchList: "teams/search/list",
            locale: "users/current/locale",
        }),
        customRoles() {
            return this.getCustomRoles;
        },
        teams() {
            return this.teamsSearchList.map(team => ({
                _id: team._id,
                type: "team",
                name: team.name,
                desc: this.$t("typeahead.team"),
                picture: teamPicture,
            }));
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                if (newRoute?.name === "SkillCreate") {
                    this.currentSkill = {
                        type: "team",
                        order: this.maxOrder + 1,
                        name: "",
                        description: "",
                        translations: {
                            [this.locale]: {},
                        },
                        descriptionCustomRoles: null,
                    };
                } else {
                    this.currentSkill = this.getSkillById(newRoute?.params?.id);
                }

                const descriptionCustomRoles = {};
                this.customRoles.forEach(customRole => {
                    if (
                        this.currentSkill.descriptionCustomRoles &&
                        this.currentSkill.descriptionCustomRoles[customRole._id]
                    ) {
                        descriptionCustomRoles[
                            customRole._id
                        ] = this.currentSkill.descriptionCustomRoles[customRole._id];
                    } else {
                        descriptionCustomRoles[customRole._id] = {
                            main: "",
                        };
                    }
                });
                this.currentSkill.descriptionCustomRoles = descriptionCustomRoles;
            },
        },
        "currentSkill.name": {
            handler(name) {
                if (!this.currentSkill.translations[this.locale]) {
                    this.currentSkill.translations[this.locale] = {};
                }

                this.currentSkill.translations[this.locale].name = name;
            },
        },
        "currentSkill.description": {
            handler(description) {
                if (!this.currentSkill.translations[this.locale]) {
                    this.currentSkill.translations[this.locale] = {};
                }

                this.currentSkill.translations[this.locale].description = description;
            },
        },
        "currentSkill.descriptionCustomRoles": {
            deep: true,
            handler(descriptionCustomRoles) {
                Object.keys(descriptionCustomRoles).forEach(customRole => {
                    if (!this.currentSkill.translations[this.locale]) {
                        this.currentSkill.translations[this.locale] = {};
                    }
                    if (!this.currentSkill.translations[this.locale].descriptionCustomRoles) {
                        this.currentSkill.translations[this.locale].descriptionCustomRoles = {};
                    }
                    if (
                        !this.currentSkill.translations[this.locale].descriptionCustomRoles[
                            customRole
                        ]
                    ) {
                        this.currentSkill.translations[this.locale].descriptionCustomRoles[
                            customRole
                        ] = {};
                    }

                    this.currentSkill.translations[this.locale].descriptionCustomRoles[
                        customRole
                    ].main = descriptionCustomRoles[customRole].main;
                });
            },
        },
    },
    created() {
        this.icon = icon;
    },
    methods: {
        changeType() {
            if (this.currentSkill.type === "localTeam") {
                this.currentSkill.type = "team";
            } else {
                this.currentSkill.type = "localTeam";
            }
        },
        onTeamSelect(team) {
            if (!this.currentSkill.localTeam) {
                this.currentSkill.localTeam = [];
            }
            if (!this.currentSkill.localTeam.find(p => p._id === team._id)) {
                this.currentSkill.localTeam.push(team);
            }
            this.$store.dispatch("teams/search/reset");
        },
        removeTeam(index) {
            this.currentSkill.localTeam.splice(index, 1);
        },
        onTeamChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("teams/search/reset");
            }
        },
        toggleOnlyUsedInReviews() {
            this.currentSkill.onlyUsedInReviews = !this.currentSkill.onlyUsedInReviews;
        },
        toggleOnlyEvaluatedByTeamMembers() {
            this.currentSkill.onlyEvaluatedByTeamMembers = !this.currentSkill
                .onlyEvaluatedByTeamMembers;
        },
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith(`input-`) ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        upsert() {
            if (!this.isValid()) {
                return;
            }

            this.$store
                .dispatch("skills/updateSkill", {
                    skill: {
                        ...this.currentSkill,
                        name:
                            this.currentSkill.translations?.["en"]?.name ?? this.currentSkill.name,
                        description:
                            this.currentSkill.translations?.["en"]?.description ??
                            this.currentSkill.description,
                    },
                })
                .then(() => {
                    this.$router.push("/skills");
                });
        },

        openTranslationModal(key, inputType) {
            this.key = key;
            this.inputType = inputType;
            this.translations = Object.keys(this.currentSkill.translations).reduce((acc, cur) => {
                acc[cur] = getVal(this.currentSkill.translations[cur], key);
                return acc;
            }, {});
            this.translations[this.locale] = getVal(this.currentSkill, key);

            if (!this.translations["en"]) {
                this.translations["en"] = "";
            }

            this.showTranslationModal = true;
        },

        updateTranslations(translations) {
            // Add/Update current skill's translations
            Object.keys(translations).forEach(lang => {
                if (!this.currentSkill.translations[lang]) {
                    this.currentSkill.translations[lang] = {};
                }

                if (this.key.startsWith("descriptionCustomRoles")) {
                    if (!this.currentSkill.translations[lang].descriptionCustomRoles) {
                        this.currentSkill.translations[lang].descriptionCustomRoles = {};
                    }

                    if (
                        !this.currentSkill.translations[lang].descriptionCustomRoles[
                            this.key.split(".")[1]
                        ]
                    ) {
                        this.currentSkill.translations[lang].descriptionCustomRoles[
                            this.key.split(".")[1]
                        ] = { main: "" };
                    }

                    this.currentSkill.translations[lang].descriptionCustomRoles[
                        this.key.split(".")[1]
                    ].main = translations[lang];
                } else {
                    this.currentSkill.translations[lang][this.key] = translations[lang];
                }
            });

            if (this.key.startsWith("descriptionCustomRoles")) {
                if (!this.currentSkill.descriptionCustomRoles) {
                    this.currentSkill.descriptionCustomRoles = {};
                }
                if (!this.currentSkill.descriptionCustomRoles[this.key.split(".")[1]]) {
                    this.currentSkill.descriptionCustomRoles[this.key.split(".")[1]] = { main: "" };
                }

                this.currentSkill.descriptionCustomRoles[this.key.split(".")[1]].main =
                    translations[this.locale];
            } else {
                this.currentSkill[this.key] = translations[this.locale];
            }

            // Delete translations
            Object.keys(this.currentSkill.translations).forEach(lang => {
                if (!translations[lang]) {
                    if (this.key.startsWith("descriptionCustomRoles")) {
                        delete this.currentSkill.translations[lang].descriptionCustomRoles[
                            this.key.split(".")[1]
                        ];
                    } else {
                        delete this.currentSkill.translations[lang][this.key];
                    }

                    if (!Object.keys(this.currentSkill.translations[lang]).length) {
                        delete this.currentSkill.translations[lang];
                    }
                }
            });

            this.showTranslationModal = false;
        },
    },
};
</script>

<style scoped>
form > div.form-group {
    margin-top: 20px;
}
.form-group .togglebutton {
    margin-top: -15px;
    margin-left: -5px;
}
span.team {
    padding: 4px 8px;
    margin-right: 8px;
}
.error {
    color: #f44336;
}
</style>
